import React, { useState, useEffect, useRef } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { getLeaseRecordsApiErrorMessage } from '../../LeaseRecordsApiError';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseInvoice } from 'src/models/dlcLease/ILeaseInvoice';
import { ILeaseCheck } from 'src/models/dlcLease/ILeaseCheck';
import CheckDetailsModal from 'src/components/invoicing/components/CheckDetailsModal';
import { FileText, Loader2, X, AlertTriangle } from 'src/components/common/icons';
import { DateField, Field, SelectField } from '../Master';
import useCheckNumberValidation from './useCheckNumberValidation';

interface ICreateWithholdingCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    leaseMaster: ILeaseMaster;
    invoice: ILeaseInvoice | null;
}

interface ICompany {
    id: number;
    name: string;
}

// Data shape matching usp_PopulateWithholdingCheck output
interface IWithholdingCheckData {
    payee: string;
    addr1: string;
    addr2: string;
    cstz: string;
    bonus: number;
    rental: number;
    other: number;
    bank: number;
    fwdBank: number;
    invoiceNo: string;
    invoiceTotal: number;
    remainingTotal: number;
    total: number;
    compId: number;
    checkDate: string;
    withholdingPercent: number; // e.g. 0.28
    withholdingAmount: number;

    // User Input Fields
    checkNo: string;
    memo: string;
    checkTotal: number; // Calculated (Total - WithholdingAmount)
}

const CreateWithholdingCheckModal: React.FC<ICreateWithholdingCheckModalProps> = ({ isOpen, onClose, onSuccess, leaseMaster, invoice }) => {
    const { post, get, response } = useFetch();
    const isMounted = useRef(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<IWithholdingCheckData | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [companies, setCompanies] = useState<ICompany[]>([]);

    const { isValidating, validationError, isCheckAvailable } = useCheckNumberValidation({
        checkNo: formData?.checkNo,
        compId: formData?.compId,
    });

    const [createdCheck, setCreatedCheck] = useState<ILeaseCheck | null>(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isOpen && invoice && leaseMaster) {
            const init = async () => {
                await populateCheckData();
            };
            init();
        } else {
            setFormData(null);
            setErrorMsg(null);
            setCreatedCheck(null);
            setCompanies([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, invoice, leaseMaster]);

    // --- Recalculation Logic ---
    useEffect(() => {
        if (formData) {
            // 1. Calculate 'Total' (Sum of items)
            const sumItems = (formData.bonus || 0) + (formData.rental || 0) + (formData.other || 0) + (formData.bank || 0) + (formData.fwdBank || 0);

            // 2. Calculate 'Backup Withholding' (Total * Percent)
            const backupWithholding = Number((sumItems * formData.withholdingPercent).toFixed(2));

            // 3. Calculate 'Check Total' (Total - Backup Withholding)
            const finalCheckTotal = sumItems - backupWithholding;

            if (formData.total !== sumItems || formData.withholdingAmount !== backupWithholding || formData.checkTotal !== finalCheckTotal) {
                setFormData((prev) =>
                    prev
                        ? {
                              ...prev,
                              total: sumItems,
                              withholdingAmount: backupWithholding,
                              checkTotal: finalCheckTotal,
                          }
                        : null
                );
            }
        }
        // Recalculate only when an amount component changes; including the complete
        // form object would rerun this effect after it writes the calculated totals.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.bonus, formData?.rental, formData?.other, formData?.bank, formData?.fwdBank, formData?.withholdingPercent]);

    const populateCheckData = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const inputDto = {
                LiTg: invoice?.liTg || 0,
                ClNo: leaseMaster.clNo || 0,
                PrNo: leaseMaster.prNo || 0,
                LsNo: leaseMaster.lsNo,
            };

            const checkDataPromise = post('/v1.0/leases/master/populate-withholding-check', inputDto);
            const companiesPromise = get('/v1.0/leases/master/companies');

            const [checkData, companiesData] = await Promise.all([checkDataPromise, companiesPromise]);

            if (!isMounted.current) {
                return;
            }

            if (response.ok && checkData) {
                let targetCompId = checkData.compId || 0;

                if (Array.isArray(companiesData)) {
                    setCompanies(companiesData);
                    if (targetCompId === 0 && companiesData.length > 0) {
                        targetCompId = companiesData[0].id;
                    }
                }

                // Initial calculation for check total
                const checkAmt = (checkData.total || 0) - (checkData.withholdingAmount || 0);

                setFormData({
                    payee: checkData.payee || '',
                    addr1: checkData.addr1 || '',
                    addr2: checkData.addr2 || '',
                    cstz: checkData.cstz || '',
                    bonus: checkData.bonus || 0,
                    rental: checkData.rental || 0,
                    other: checkData.other || 0,
                    bank: checkData.bank || 0,
                    fwdBank: checkData.fwdBank || 0,
                    invoiceNo: checkData.invoiceNo || '',
                    invoiceTotal: checkData.invoiceTotal || 0,
                    remainingTotal: checkData.remainingTotal || 0,
                    total: checkData.total || 0,
                    compId: targetCompId,
                    checkDate: checkData.checkDate ? checkData.checkDate.split('T')[0] : new Date().toISOString().split('T')[0],
                    withholdingPercent: checkData.withholdingPercent || 0.28,
                    withholdingAmount: checkData.withholdingAmount || 0,
                    checkNo: '',
                    memo: '',
                    checkTotal: checkAmt,
                });
            } else {
                setErrorMsg('Failed to load check details.');
            }
        } catch (error) {
            if (isMounted.current) {
                setErrorMsg('Error connecting to server.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleSave = async () => {
        if (!formData) {
            return;
        }

        if (validationError) {
            alert(`Cannot save: ${validationError}`);
            return;
        }

        if (!isCheckAvailable) {
            alert('Please enter a valid, available Check Number.');
            return;
        }

        const safeCheckNo = parseInt(formData.checkNo, 10);
        if (isNaN(safeCheckNo) || safeCheckNo <= 0) {
            alert('Please enter a valid Check Number.');
            return;
        }

        if (formData.checkTotal <= 0) {
            alert('Check Total must be greater than zero.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                CheckNo: safeCheckNo,
                CheckDate: formData.checkDate,
                CheckAmount: formData.checkTotal, // Net Amount (mapped to @CheckAmount in SP?) No, usually passed implicitly or as param
                // Checking SP parameters from your provided code:
                // @CheckNo, @CheckDate, @Bonus, @Rental, @Bank, @FwdBank, @Other, @Total, @RemTotal, @Withholding_Amount, @CompID, ...
                // The SP calculates @v_total inside, but accepts @Total param. We pass values as requested.

                CompID: formData.compId,
                LeaseNo: leaseMaster.lsNo,
                InvoiceNo1: 0, // Passed as 0 since we might not have pure int from 'invoiceNo' string
                InvoiceAdd: '',
                LiTg: invoice?.liTg || 0,
                Bonus: formData.bonus,
                Rental: formData.rental,
                Bank: formData.bank,
                FwdBank: formData.fwdBank,
                Other: formData.other,
                Total: formData.total, // Gross Total passed to @Total
                RemTotal: formData.remainingTotal, // Passed to @RemTotal
                WithholdingAmount: formData.withholdingAmount, // Passed to @Withholding_Amount

                // Details
                Payee: formData.payee,
                Addr1: formData.addr1,
                Addr2: formData.addr2,
                CSTZ: formData.cstz,
                MemoFor: formData.memo,
            };

            await post('/v1.0/leases/master/process-withholding-check', payload);

            if (!isMounted.current) {
                return;
            }

            if (response.ok) {
                onSuccess?.();
                // Mock success behavior: try to fetch check or just close
                const checkDetails = await get(`/v1.0/leases/master/checks/${safeCheckNo}?compId=${payload.CompID}`);
                if (response.ok && checkDetails && checkDetails.length > 0) {
                    setCreatedCheck(checkDetails[0]);
                } else {
                    onClose();
                }
            } else {
                setErrorMsg(getLeaseRecordsApiErrorMessage(response.data, response.status));
            }
        } catch (error) {
            if (isMounted.current) {
                setErrorMsg(getLeaseRecordsApiErrorMessage(undefined, 500));
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleChange = (field: keyof IWithholdingCheckData, value: string | number) => {
        if (!formData) {
            return;
        }
        setFormData({ ...formData, [field]: value });
    };

    const renderCheckNoLabel = () => {
        return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>Check No.</span>
                <span style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', fontSize: '0.75rem', fontWeight: 600 }}>
                    {isValidating && (
                        <>
                            <Loader2 style={styles.spinner} />
                            <span style={{ color: '#6B7280' }}>Checking...</span>
                        </>
                    )}
                    {!isValidating && validationError && <span style={styles.errorText}>{validationError}</span>}
                    {!isValidating && isCheckAvailable && <span style={styles.successText}>Available</span>}
                </span>
            </span>
        );
    };

    if (!isOpen) {
        return null;
    }

    if (createdCheck) {
        return <CheckDetailsModal check={createdCheck} onClose={onClose} />;
    }

    // Fix Duplicate Select
    const companyOptions = companies.map((c) => ({ value: c.id.toString(), label: c.name }));
    if (!formData?.compId) {
        companyOptions.unshift({ value: '', label: 'Select...' });
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <FileText style={styles.headerIcon} />
                        Create Withholding Check
                    </h2>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.modalBody}>
                    {loading && !formData ? (
                        <div style={styles.loadingState}>
                            <Loader2 style={styles.loaderIcon} />
                            <span style={styles.loadingText}>Loading check details...</span>
                        </div>
                    ) : errorMsg ? (
                        <div style={styles.errorState} role="alert">
                            <AlertTriangle style={styles.errorIcon} />
                            Error: {errorMsg}
                        </div>
                    ) : formData ? (
                        <>
                            <div style={styles.gridContainer}>
                                {/* Col 1 */}
                                <div style={styles.col}>
                                    <Field label="Lease No." value={leaseMaster.lsNo.toString()} editable={false} />

                                    <Field
                                        label={renderCheckNoLabel() as unknown as string}
                                        value={formData.checkNo}
                                        onChange={(val) => handleChange('checkNo', val)}
                                        editable={true}
                                    />

                                    <Field
                                        label="Bonus"
                                        type="number"
                                        prefix="$"
                                        value={formData.bonus.toString()}
                                        onChange={(val) => handleChange('bonus', parseFloat(val) || 0)}
                                        editable={true}
                                    />
                                    <Field
                                        label="Bank Fees"
                                        type="number"
                                        prefix="$"
                                        value={formData.bank.toString()}
                                        onChange={(val) => handleChange('bank', parseFloat(val) || 0)}
                                        editable={true}
                                    />

                                    {/* YELLOW Highlight Total */}
                                    <div style={styles.highlightYellow}>
                                        <Field label="Total" value={formData.total.toFixed(2)} prefix="$" editable={false} />
                                    </div>
                                </div>

                                {/* Col 2 */}
                                <div style={styles.col}>
                                    <Field label="Invoice No." value={formData.invoiceNo} editable={false} />
                                    <DateField
                                        label="Check Date"
                                        value={formData.checkDate}
                                        onChange={(val) => handleChange('checkDate', val)}
                                        editable={true}
                                    />
                                    <Field
                                        label="Other"
                                        type="number"
                                        prefix="$"
                                        value={formData.other.toString()}
                                        onChange={(val) => handleChange('other', parseFloat(val) || 0)}
                                        editable={true}
                                    />
                                    <Field
                                        label="Fwd. Bank Fees"
                                        type="number"
                                        prefix="$"
                                        value={formData.fwdBank.toString()}
                                        onChange={(val) => handleChange('fwdBank', parseFloat(val) || 0)}
                                        editable={true}
                                    />

                                    {/* RED Highlight Backup Withholding */}
                                    <div style={styles.highlightRed}>
                                        <Field label="Backup Withholding" value={formData.withholdingAmount.toFixed(2)} prefix="$" editable={false} />
                                    </div>
                                </div>

                                {/* Col 3 */}
                                <div style={styles.col}>
                                    <Field label="Invoice Total" value={formData.invoiceTotal.toFixed(2)} prefix="$" editable={false} />
                                    <Field label="Remain Total" value={formData.remainingTotal.toFixed(2)} prefix="$" editable={false} />
                                    <Field
                                        label="Rentals"
                                        type="number"
                                        prefix="$"
                                        value={formData.rental.toString()}
                                        onChange={(val) => handleChange('rental', parseFloat(val) || 0)}
                                        editable={true}
                                    />
                                    <Field
                                        label="Withholding %"
                                        type="number"
                                        value={formData.withholdingPercent.toString()}
                                        onChange={(val) => handleChange('withholdingPercent', parseFloat(val) || 0)}
                                        editable={true}
                                    />

                                    {/* GREEN Highlight Check Total */}
                                    <div style={styles.highlightGreen}>
                                        <Field label="Check Total" value={formData.checkTotal.toFixed(2)} prefix="$" editable={false} />
                                    </div>
                                </div>
                            </div>

                            <div style={styles.divider} />

                            <div style={styles.fullWidthContainer}>
                                <div style={styles.row}>
                                    <SelectField
                                        label="Company"
                                        value={formData.compId.toString()}
                                        onChange={(val) => handleChange('compId', parseInt(val, 10) || 0)}
                                        editable={true}
                                        options={companyOptions}
                                    />
                                </div>
                                <div style={styles.row}>
                                    <Field label="Payee" value={formData.payee} onChange={(val) => handleChange('payee', val)} editable={true} />
                                </div>
                                <div style={styles.row}>
                                    <Field label="Address" value={formData.addr1} onChange={(val) => handleChange('addr1', val)} editable={true} />
                                </div>
                                <div style={styles.row}>
                                    <Field label="" value={formData.addr2} onChange={(val) => handleChange('addr2', val)} editable={true} />
                                </div>
                                <div style={styles.row}>
                                    <Field
                                        label="City, ST, Zip"
                                        value={formData.cstz}
                                        onChange={(val) => handleChange('cstz', val)}
                                        editable={true}
                                    />
                                </div>
                                <div style={styles.row}>
                                    <Field label="Memo" value={formData.memo} onChange={(val) => handleChange('memo', val)} editable={true} />
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>

                {/* Footer */}
                <div style={styles.modalFooter}>
                    <button onClick={onClose} style={styles.buttonSecondary} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        style={{
                            ...styles.buttonPrimary,
                            opacity: !isCheckAvailable || loading ? 0.5 : 1,
                            cursor: !isCheckAvailable || loading ? 'not-allowed' : 'pointer',
                        }}
                        disabled={loading || !formData || !!validationError || isValidating || !isCheckAvailable}
                    >
                        {loading ? 'Processing...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Styles ---
const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.25rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', margin: 0 },
    headerIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem', color: '#4F46E5' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' },
    gridContainer: { display: 'flex', gap: '1.5rem', marginBottom: '1.5rem' },
    col: { flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' },
    fullWidthContainer: { display: 'flex', flexDirection: 'column', gap: '1rem' },
    row: { display: 'flex', flexDirection: 'column' },
    divider: { height: '1px', backgroundColor: '#E5E7EB', width: '100%', margin: '1.5rem 0' },
    modalFooter: { padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    buttonSecondary: {
        padding: '0.6rem 1.5rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.5rem',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    loadingState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '12.5rem', color: '#4F46E5' },
    loaderIcon: { width: '2rem', height: '2rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite' },
    loadingText: { fontSize: '1.125rem', fontWeight: 500 },
    errorState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '8rem',
        color: '#EF4444',
        fontWeight: 500,
        backgroundColor: '#FEF2F2',
        borderRadius: '0.5rem',
        padding: '1rem',
    },
    errorIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' },
    spinner: { width: '0.9rem', height: '0.9rem', animation: 'spin 1s linear infinite', color: '#6B7280', marginRight: '4px' },
    errorText: { color: '#EF4444' },
    successText: { color: '#10B981' },

    // Highlight Styles
    highlightYellow: { backgroundColor: '#FEF9C3', padding: '0.25rem', borderRadius: '0.25rem', border: '1px solid #FDE047' },
    highlightRed: { backgroundColor: '#FEE2E2', padding: '0.25rem', borderRadius: '0.25rem', border: '1px solid #FECACA' },
    highlightGreen: { backgroundColor: '#DCFCE7', padding: '0.25rem', borderRadius: '0.25rem', border: '1px solid #BBF7D0' },
};

export default CreateWithholdingCheckModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/checks/CreateWithholdingCheckModal.tsx